import type { GroupPeek, GroupSummary } from '@geosport/shared';
import { useState } from 'react';
import { Button } from '../components/Button';
import { Shell } from '../components/Shell';
import { track } from '../lib/analytics';
import { api, ApiError } from '../lib/api';
import { Link, navigate } from '../lib/router';
import { useApi } from '../lib/useApi';

/** Landing page for an invite link: /join/<code>. */
export function Join({ code }: { code: string }) {
  const { data, error, loading } = useApi<GroupPeek>(`/groups/peek/${encodeURIComponent(code)}`);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const join = async (): Promise<void> => {
    setBusy(true);
    try {
      const g = await api<GroupSummary>('/groups/join', { method: 'POST', body: { code } });
      track('group_joined');
      navigate(`/groups/${g.id}`, { replace: true });
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : 'Could not join the group');
      setBusy(false);
    }
  };

  return (
    <Shell title="Join a group">
      {loading && !data && <div className="skeleton h-28 rounded-2xl" aria-hidden="true" />}
      {error && (
        <div className="rounded-2xl bg-red-500/10 p-4 text-sm text-red-200 ring-1 ring-red-500/30">
          <p>{error}</p>
          <Link to="/groups" className="mt-2 inline-block text-accent">
            Go to groups
          </Link>
        </div>
      )}
      {data && (
        <div className="rounded-2xl bg-ink-2 p-5 ring-1 ring-white/10">
          <p className="text-sm text-white/60">You’ve been invited to</p>
          <h2 className="text-2xl font-bold" data-testid="join-name">
            {data.name}
          </h2>
          <p className="text-sm text-white/60">
            {data.members} {data.members === 1 ? 'member' : 'members'} · compare daily scores
          </p>
          <Button
            className="mt-4 w-full"
            onClick={() => void join()}
            disabled={busy}
            data-testid="join-button"
          >
            {data.alreadyMember ? 'Open group' : 'Join group'}
          </Button>
          {message && (
            <p className="mt-2 text-sm text-red-300" role="status">
              {message}
            </p>
          )}
        </div>
      )}
    </Shell>
  );
}
